import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaFacebookF, FaTwitter, FaLinkedinIn, FaYoutube, FaInstagram, FaClock, FaFileAlt } from 'react-icons/fa';
import { IconWrapper } from '../icons/IconWrapper';

const FooterContainer = styled.footer`
  background-color: ${({ theme }) => theme.colors.darkest};
  color: ${({ theme }) => theme.colors.light};
  padding: 4rem 0 2rem;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  padding: 0 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const FooterSection = styled.div`
  h3 {
    color: ${({ theme }) => theme.colors.light};
    margin-bottom: 1.5rem;
    font-size: ${({ theme }) => theme.fontSizes.large};
    position: relative;
    padding-bottom: 0.75rem;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      height: 3px;
      width: 50px;
      background: linear-gradient(
        to right,
        ${({ theme }) => theme.colors.primary},
        ${({ theme }) => theme.colors.secondary}
      );
    }
  }
`;

const FooterLogo = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem;
  
  img {
    margin-right: -7px;
  }
  
  div {
    margin-top: 10px;
  }
  
  span.elite {
    color: ${({ theme }) => theme.colors.light};
    font-size: 1.5rem;
    line-height: 2;
  }
  
  span.wire {
    color: ${({ theme }) => theme.colors.primary};
    font-size: 1.5rem;
    line-height: 2;
  }
`;

const FooterText = styled.p`
  margin-bottom: 1.5rem;
  font-size: ${({ theme }) => theme.fontSizes.medium};
  line-height: 1.6;
`;

const ContactInfo = styled.div`
  margin-bottom: 1.5rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem;
  
  svg {
    margin-right: 1rem;
    margin-top: 0.25rem;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

// Add a specific style for the email item
const EmailContactItem = styled(ContactItem)`
  align-items: center;
  
  svg {
    margin-top: 0;
  }
`;

const FooterLinks = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FooterLinkItem = styled.li`
  margin-bottom: 0.75rem;
`;

const FooterLink = styled(Link)`
  color: ${({ theme }) => theme.colors.light};
  transition: ${({ theme }) => theme.transitions.short};
  display: inline-block;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.primary};
    transition: ${({ theme }) => theme.transitions.short};
  }
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    
    &::after {
      width: 100%;
    }
  }
`;

const ExternalFooterLink = styled.a`
  color: ${({ theme }) => theme.colors.light};
  transition: ${({ theme }) => theme.transitions.short};
  display: inline-block;
  position: relative;
  text-decoration: none;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 2px;
    background-color: ${({ theme }) => theme.colors.primary};
    transition: ${({ theme }) => theme.transitions.short};
  }
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    
    &::after {
      width: 100%;
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const SocialIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: rgba(255, 255, 255, 0.05);
  color: ${({ theme }) => theme.colors.light};
  border-radius: ${({ theme }) => theme.borderRadius.round};
  transition: ${({ theme }) => theme.transitions.short};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.light};
    transform: translateY(-3px);
  }
`;

const Divider = styled.hr`
  border: 0;
  height: 1px;
  background-color: rgba(255, 255, 255, 0.05);
  margin: 2rem auto;
  max-width: 1200px;
`;

const Copyright = styled.div`
  text-align: center;
  padding: 0 1rem;
  font-size: ${({ theme }) => theme.fontSizes.small};
  color: rgba(255, 255, 255, 0.7);
  max-width: 1200px;
  margin: 0 auto;
  
  a {
    color: ${({ theme }) => theme.colors.primary};
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <FooterContainer>
      <FooterContent>
        <FooterSection>
          <FooterLogo>
            <img src="/images/logo.svg" className="logo-shape" alt="Logo" style={{ width: "55px", height: "55px" }} />
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', marginTop: '10px' }}>
              <span className="elite">ELECTRO</span>
              <span className="wire">CABLE</span>
            </div>
          </FooterLogo>
          <FooterText>
            Explore our comprehensive online catalog for a wide array of wire and cable products.
            Access detailed spec tables and in-depth information on individual product pages,
            designed to help you make informed decisions for your specific project needs.
            Our website is your go-to resource for exploring our full range of solutions,
            complete with specifications and detailed descriptions to ensure you find exactly what
            you're looking for.
          </FooterText>
          <SocialLinks>
            <SocialIcon href="https://facebook.com" target="_blank" rel="noopener noreferrer">
              <IconWrapper icon={FaFacebookF} size={16} />
            </SocialIcon>
            <SocialIcon href="https://twitter.com" target="_blank" rel="noopener noreferrer">
              <IconWrapper icon={FaTwitter} size={16} />
            </SocialIcon>
            <SocialIcon href="https://linkedin.com" target="_blank" rel="noopener noreferrer">
              <IconWrapper icon={FaLinkedinIn} size={16} />
            </SocialIcon>
            <SocialIcon href="https://youtube.com" target="_blank" rel="noopener noreferrer">
              <IconWrapper icon={FaYoutube} size={16} />
            </SocialIcon>
            <SocialIcon href="https://instagram.com" target="_blank" rel="noopener noreferrer">
              <IconWrapper icon={FaInstagram} size={16} />
            </SocialIcon>
          </SocialLinks>
        </FooterSection>

        <FooterSection>
          <h3>Quick Links</h3>
          <FooterLinks>
            <FooterLinkItem>
              <FooterLink to="/">Home</FooterLink>
            </FooterLinkItem>
            <FooterLinkItem>
              <FooterLink to="/about">About Us</FooterLink>
            </FooterLinkItem>
            <FooterLinkItem>
              <FooterLink to="/products">Products</FooterLink>
            </FooterLinkItem>
            <FooterLinkItem>
              <FooterLink to="/industries">Industries</FooterLink>
            </FooterLinkItem>
            <FooterLinkItem>
              <FooterLink to="/contact">Contact</FooterLink>
            </FooterLinkItem>
            <FooterLinkItem>
              <ExternalFooterLink
                href="https://drive.google.com/file/d/1_Ol2Ee_4fbg8jO4UbGYlYfv0KJ4v-ohB/view?usp=sharing"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.preventDefault();
                  window.open("https://drive.google.com/file/d/1_Ol2Ee_4fbg8jO4UbGYlYfv0KJ4v-ohB/view?usp=sharing", '_blank', 'noopener,noreferrer');
                }}
              >
                <span style={{ marginRight: '0.4rem', display: 'inline-flex' }}><IconWrapper icon={FaFileAlt} size={12} /></span>
                Line Card
              </ExternalFooterLink>
            </FooterLinkItem>
          </FooterLinks>
        </FooterSection>

        <FooterSection>
          <h3>Products</h3>
          <FooterLinks>
            <FooterLinkItem>
              <FooterLink to="/products?category=aluminum-building-wire">Aluminum Building Wire</FooterLink>
            </FooterLinkItem>
            <FooterLinkItem>
              <FooterLink to="/products?category=600-volt-ud">600 Volt UD</FooterLink>
            </FooterLinkItem>
            <FooterLinkItem>
              <FooterLink to="/products?category=service-drop">Service Drop</FooterLink>
            </FooterLinkItem>
            <FooterLinkItem>
              <FooterLink to="/products?category=bare-aluminum">Bare Aluminum</FooterLink>
            </FooterLinkItem>
            <FooterLinkItem>
              <FooterLink to="/products?category=medium-voltage">Medium Voltage</FooterLink>
            </FooterLinkItem>
            <FooterLinkItem>
              <FooterLink to="/products?category=portable-cord">Portable Cord</FooterLink>
            </FooterLinkItem>
          </FooterLinks>
        </FooterSection>

        <FooterSection>
          <h3>Contact Us</h3>
          <ContactInfo>
            <ContactItem>
              <IconWrapper icon={FaMapMarkerAlt} size={20} />
              <div>
                10408 West Mcnab Road Tamarac, FL 33321<br />
                United States
              </div>
            </ContactItem>
            <ContactItem>
              <IconWrapper icon={FaPhone} size={20} />
              <div>
                <div>Direct: +1 847 376 9187</div>
                <div>Office: +1 954 526 8042</div>
              </div>
            </ContactItem>
            <EmailContactItem>
              <IconWrapper icon={FaEnvelope} size={20} />
              <div>
                info@electrocableus.com
              </div>
            </EmailContactItem>
            <ContactItem>
              <IconWrapper icon={FaClock} size={20} />
              <div>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Hours of Operation:</div>
                <div>7:30 A.M. - 7:00 P.M. CST</div>
                <div>Monday - Friday</div>
              </div>
            </ContactItem>
          </ContactInfo>
        </FooterSection>
      </FooterContent>

      <Divider />

      <Copyright>
        <p>© {currentYear} ElectroCable. All Rights Reserved.</p>
        <p>
          <Link to="/privacy-policy">Privacy Policy</Link> |
          <Link to="/terms-of-service"> Terms of Service</Link>
        </p>
      </Copyright>
    </FooterContainer>
  );
};

export default Footer; 